import React, { useContext, useState } from "react";
import type { FixedMapLayerKey, FixedMapLayerSettings } from "../utils/mapLayerPreferences";

export type MapType = "events" | "venues" | "zones";
export type InfrastructureVisibilityMode = "auto" | "show" | "hide";

export type MapDrawModeQualifier = {
    mode: "draw_point" | "draw_polygon" | "simple_select";
    name: string,
    tableName: string;
    key: string;
    value: string;
};

export type MapFeatureSelectionMeta = {
    tableName: string;
    key: string;
    value: number | string;
    name: string;
};

export interface MapContextType {
    mapContainerRef: React.RefObject<HTMLDivElement | null>;
    setMapContainerRef: React.Dispatch<React.SetStateAction<React.RefObject<HTMLDivElement | null>>>;
    mapRef: React.RefObject<mapboxgl.Map | null>;
    setMapRef: React.Dispatch<React.SetStateAction<React.RefObject<mapboxgl.Map | null>>>;
    drawRef: React.RefObject<MapboxDraw | null>;
    setDrawRef: React.Dispatch<React.SetStateAction<React.RefObject<MapboxDraw | null>>>;
    flyTo: (lng: number, lat: number, zoom?: number) => void;
    setMarker: (lng: number, lat: number) => void;
    setDrawMode: (mode: "draw_point" | "draw_polygon" | "simple_select") => void;
    updateImageryLayers: () => Promise<void>;
    updateLayerOpacity: (imagery_source_id: number, opacity: number) => void;
    updateFixedMapLayer: (layerKey: FixedMapLayerKey, settings: FixedMapLayerSettings) => void;
    mapType: MapType;
    setMapType: (type: MapType) => void;
    mapName: string | null;
    setMapName: (name: string | null) => void;
    mapParentName: string | null;
    setMapParentName: (name: string | null) => void;
    setDrawQualifier: (qualifier: MapDrawModeQualifier) => void;
    drawQualifier: MapDrawModeQualifier;
    // y-coordinate (in pixels) for an overlay control panel anchored to the map,
    // expressed relative to the top of the map container, and its visibility.
    controlPanelY: number | null;
    setControlPanelY: (y: number | null) => void;
    controlPanelVisible: boolean;
    setControlPanelVisible: (visible: boolean) => void;
    currentFeatureMeta?: MapFeatureSelectionMeta;
    setCurrentFeatureMeta: (meta?: MapFeatureSelectionMeta) => void;
    infrastructureVisibilityMode: InfrastructureVisibilityMode;
    setInfrastructureVisibilityMode: (mode: InfrastructureVisibilityMode) => void;
};

const MapContext = React.createContext<MapContextType>({
    mapContainerRef: React.createRef(),
    setMapContainerRef: () => { },
    mapRef: React.createRef(),
    setMapRef: () => { },
    drawRef: React.createRef(),
    setDrawRef: () => { },
    flyTo: () => { },
    setMarker: () => { },
    setDrawMode: () => { },
    updateImageryLayers: async () => { },
    updateLayerOpacity: () => { },
    updateFixedMapLayer: () => { },
    mapType: "events",
    setMapType: () => { },
    mapName: null,
    setMapName: () => { },
    mapParentName: null,
    setMapParentName: () => { },
    setDrawQualifier: () => { },
    drawQualifier: { name: "[Object]", mode: "simple_select", tableName: "", key: "", value: "" },
    controlPanelY: null,
    setControlPanelY: () => { },
    controlPanelVisible: false,
    setControlPanelVisible: () => { },
    currentFeatureMeta: undefined,
    setCurrentFeatureMeta: () => { },
    infrastructureVisibilityMode: "auto",
    setInfrastructureVisibilityMode: () => { },
});

export const MapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mapType, setMapType] = useState<MapType>("events");
    const [mapName, setMapName] = useState<string | null>(null);
    const [mapParentName, setMapParentName] = useState<string | null>(null);
    const [drawQualifier, setDrawQualifier] = useState<MapDrawModeQualifier>({ name: "[Object]", mode: "simple_select", tableName: "", key: "", value: "" });
    const [controlPanelY, setControlPanelY] = useState<number | null>(null);
    const [controlPanelVisible, setControlPanelVisible] = useState<boolean>(false);
    const [currentFeatureMeta, setCurrentFeatureMeta] = useState<MapFeatureSelectionMeta | undefined>(undefined);
    const [infrastructureVisibilityMode, setInfrastructureVisibilityMode] = useState<InfrastructureVisibilityMode>("auto");

    const [mapContainerRef] = useState<React.RefObject<HTMLDivElement | null>>(() => React.createRef());
    const [mapRef] = useState<React.RefObject<mapboxgl.Map | null>>(() => React.createRef());
    const [drawRef] = useState<React.RefObject<MapboxDraw | null>>(() => React.createRef());

    return (
        <MapContext.Provider value={{
            mapContainerRef,
            setMapContainerRef: () => { },
            mapRef,
            setMapRef: () => { },
            drawRef,
            setDrawRef: () => { },
            flyTo: () => { },
            setMarker: () => { },
            setDrawMode: () => { },
            updateImageryLayers: async () => { },
            updateLayerOpacity: () => { },
            updateFixedMapLayer: () => { },
            mapType, setMapType,
            mapName, setMapName,
            mapParentName, setMapParentName,
            setDrawQualifier, drawQualifier,
            controlPanelY, setControlPanelY,
            controlPanelVisible, setControlPanelVisible,
            currentFeatureMeta, setCurrentFeatureMeta,
            infrastructureVisibilityMode, setInfrastructureVisibilityMode,
        }}>
            {children}
        </MapContext.Provider>
    );
};

export const useMapContext = () => {
    const ctx = useContext(MapContext);
    if (!ctx) throw new Error("useMapContext must be used inside MapProvider");
    return ctx;
};
